/**
 * @file ThemeContext.js
 * @description This file contains the implementation of the ThemeContext and ThemeProvider components.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuthContext } from './AuthContext';
import { darkenColor, hexToRgb, lightenColor } from '../utils/utils';
const ThemeContext = createContext();

/**
 * @description The ThemeProvider component wraps the application and provides the theme styles to its children.
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The children components.
 * @returns {React.ReactNode} The rendered component tree.
 */
export const ThemeProvider = ({ children }) => {
    const { publicBrand } = useAuthContext();
    const [themeStyles, setThemeStyles] = useState({});

    useEffect(() => {
        if (publicBrand) {
            setThemeStyles({
                tenantId: publicBrand.tenantId,
                appName: publicBrand.name,
                primaryColor: publicBrand.accessoryColor,
                secondaryColor: publicBrand.pageHeaderColor1,
                tertiaryColor: publicBrand.tertiary_color,
                primaryLogo:
                    publicBrand.primaryLogo || publicBrand.defaultLogo || null,
                secondaryLogo:
                    publicBrand.secondaryLogo ||
                    publicBrand.primaryLogo ||
                    publicBrand.defaultLogo ||
                    null,
                favicon: publicBrand.favicon || '/icons/icon-120.png',
                headerColor1: publicBrand.pageHeaderColor1,
                headerColor2:
                    publicBrand.pageHeaderColor2 ||
                    publicBrand.pageHeaderColor1,
                accessoriesBg1: publicBrand.accessoryColor,
                accessoriesBg2:
                    publicBrand?.accessoriesBg?.length > 1
                        ? publicBrand.accessoriesBg[1] ||
                          publicBrand?.accessoriesBg[0] ||
                          '#1366ff'
                        : '#1366ff',
                accessoriesFg1: publicBrand.accessoryTextColor,
                accessoriesFg2:
                    publicBrand?.accessoriesFg?.length > 1
                        ? publicBrand.accessoriesFg[1] ||
                          publicBrand?.accessoriesFg[0] ||
                          '#FFFFFF'
                        : '#FFFFFF',
                loginBackground: {
                    type: publicBrand?.loginBackgroundImage ? 'image' : 'color',
                    color: publicBrand.loginBackgroundColor || '#f8fafc',
                    image: publicBrand?.loginBackgroundImage || '',
                },
            });
        }
    }, [publicBrand]);

    useEffect(() => {
        if (themeStyles) {
            setAppTitle();
            setHeaderColors();
            setAccessoriesColors();
            setAccessoriesForegroundColors();
            setCheckboxSvgColors();
        }
    }, [themeStyles]);

    const setAppTitle = () => {
        document.title = themeStyles.appName || 'iNGAGED';
        const favicon =
            document.querySelector('link[rel="icon"]') ||
            document.createElement('link');
        favicon.type = 'image/x-icon';
        favicon.rel = 'icon';
        favicon.href = themeStyles.favicon;
        document.head.appendChild(favicon);
    };

    const setHeaderColors = () => {
        if (themeStyles.headerColor1)
            document.documentElement.style.setProperty(
                '--iNGAGED-Header-Color1',
                themeStyles.headerColor1,
            );
        if (themeStyles.headerColor2)
            document.documentElement.style.setProperty(
                '--iNGAGED-Header-Color2',
                themeStyles.headerColor2,
            );
    };

    const setAccessoriesColors = () => {
        if (themeStyles.accessoriesBg1) {
            document.documentElement.style.setProperty(
                '--iNGAGED-Accessory-Color1',
                themeStyles.accessoriesBg1,
            );
            const rgbColor1 = hexToRgb(themeStyles.accessoriesBg1);
            const darkenedColor1 = darkenColor(rgbColor1, 0.1);
            document.documentElement.style.setProperty(
                '--iNGAGED-Accessory-Hover-Color1',
                `rgb(${darkenedColor1.r}, ${darkenedColor1.g}, ${darkenedColor1.b})`,
            );
            document.documentElement.style.setProperty(
                '--iNGAGED-Accessory-disabled-Color1',
                `rgba(${darkenedColor1.r}, ${darkenedColor1.g}, ${darkenedColor1.b}, 0.8)`,
            );
            const infoSelectColor3 = lightenColor(
                themeStyles.accessoriesBg1,
                0.85,
            );
            document.documentElement.style.setProperty(
                '--iNGAGED-InfoSelect-Color3',
                infoSelectColor3,
            );
        }

        if (themeStyles.accessoriesBg2) {
            document.documentElement.style.setProperty(
                '--iNGAGED-Accessory-Color2',
                themeStyles.accessoriesBg2,
            );
            const rgbColor2 = hexToRgb(themeStyles.accessoriesBg2);
            const darkenedColor2 = darkenColor(rgbColor2, 0.1);
            document.documentElement.style.setProperty(
                '--iNGAGED-Accessory-Hover-Color2',
                `rgb(${darkenedColor2.r}, ${darkenedColor2.g}, ${darkenedColor2.b})`,
            );
        }
    };

    const setAccessoriesForegroundColors = () => {
        if (themeStyles.accessoriesFg1) {
            document.documentElement.style.setProperty(
                '--iNGAGED-AccessoryForeground-Color1',
                themeStyles.accessoriesFg1,
            );
        }

        if (themeStyles.accessoriesFg2) {
            document.documentElement.style.setProperty(
                '--iNGAGED-AccessoryForeground-Color2',
                themeStyles.accessoriesFg2,
            );
        }
    };

    const setCheckboxSvgColors = () => {
        const checkmarkColor = themeStyles.accessoriesFg1 || '#1366ff';
        const checkedSvg = createCheckboxSvg(checkmarkColor, 'checkmark');
        const indeterminateSvg = createCheckboxSvg(checkmarkColor, 'dash');

        document.documentElement.style.setProperty(
            '--bs-form-check-input-checked-bg-image',
            `url("${checkedSvg}")`,
        );
        document.documentElement.style.setProperty(
            '--bs-form-check-input-indeterminate-bg-image',
            `url("${indeterminateSvg}")`,
        );
    };

    /**
     * Creates an SVG string for a checkbox with the specified color and type,
     * and performs basic sanitization.
     *
     * @param {string} color - The fill color for the SVG.
     * @param {string} type - The type of SVG ('checkmark' or 'dash').
     * @returns {string} The sanitized SVG string.
     */
    const createCheckboxSvg = (color, type) => {
        let path = '';
        if (type === 'checkmark') {
            path = 'm6 10 3 3 6-6';
        } else if (type === 'dash') {
            path = 'M6 10h8';
        }

        const svgString2 = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='${color}' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='${path}'/></svg>`;
        const encodedSvgString = encodeURIComponent(svgString2);
        const encodedDataUri = `data:image/svg+xml,${encodedSvgString}`;
        return encodedDataUri;
    };

    return (
        <ThemeContext.Provider value={themeStyles}>
            {children}
        </ThemeContext.Provider>
    );
};

/**
 * @returns {Object} The theme styles.
 */
export const useTheme = () => useContext(ThemeContext);
